import { Framework } from '@/framework/instance';
import { IEditMessage, IRealEditConfig } from '../../types';
import { OperateType } from '../../types/operate';
import { Collaborators } from '../collaborators';
import { CRDTDocument } from '../crdt-document';
import { UserReady } from '../operate/user-ready';

interface IRoom {
    onMessage: (type: OperateType, cb: (data: IEditMessage<any>) => void) => void;
    isRealEdit: () => boolean;
    sendToUser: (wsKeys: number[], type: OperateType, data?: unknown) => Promise<void>;
    renderUserCursor: () => void;
    renderUserPick: () => void;
    emitReady: () => void;
    collaborators: Collaborators;
    config?: IRealEditConfig;
    userReady: UserReady;
    document: CRDTDocument;
}

export class SyncDocumentAdapter extends Framework {
    /** 文档数据 */
    private receivedDocumentData: unknown[] = [];

    constructor(
        identity: string,
        private room: IRoom,
        private options: {
            syncUpdateAt: (updateAt: number) => void;
        },
    ) {
        super(identity);
        // 监听同步文档数据事件
        room.onMessage(OperateType.SyncDocument, this.onReceiveDocument.bind(this));
    }

    /** 同步文档数据给指定成员 */
    async syncDocument(wsKeys: number[]) {
        if (!this.room.isRealEdit()) {
            return;
        }
        const snapshot = this.framework.getSnapshot();

        const pickData = this.room.collaborators.getHasPickInfoMembers().map((item) => {
            const { wsKey, activeId, pickId } = item.getInfo();
            return [wsKey, activeId, pickId];
        });

        const updatedAt = this.room.config?.getDocumentUpdateAt();
        // 同步文档数据给指定成员
        await this.room.sendToUser(wsKeys, OperateType.SyncDocument, [pickData, updatedAt, snapshot]);
    }

    /** 收到同步文档数据事件 */
    private onReceiveDocument({ data }: IEditMessage<[[number, string, string][], number, any]>) {
        console.log('ot onReceiveDocument', data);
        this.receivedDocumentData = data;
        this.processDocument();
    }

    processDocument() {
        if (!this.receivedDocumentData.length) return;
        if (!this.room.collaborators.getMember()) return;
        if (!this.room.userReady.send()) return;
        const [pickData, updatedAt, snapshot] = this.receivedDocumentData as [[number, string, string][], number, any];
        this.receivedDocumentData = [];
        // 接收到成员的选中图层信息
        for (const [wsKey, activeId, pickId] of pickData) {
            this.room.collaborators.getMember(wsKey)?.setPickInfo(activeId, pickId, false);
        }
        if (updatedAt) {
            const currentUpdateAt = this.room.config?.getDocumentUpdateAt();
            // console.log('ot updatedAt', { updatedAt, currentUpdateAt });
            if (currentUpdateAt) {
                if (currentUpdateAt <= updatedAt) {
                    //  接收到最新文档更新时间
                    this.room.config?.setDocumentUpdateAt(updatedAt);
                    // console.log('ot updatedAt 1', updatedAt);
                } else {
                    // 初始化文档时间大于当前同步的时间，同步时间给其他人
                    this.options.syncUpdateAt(currentUpdateAt);
                    // console.log('ot updatedAt 2', currentUpdateAt);
                }
            }
        }
        // 恢复数据
        this.framework.restoreSnapshot(snapshot);
        const createStagesFontFamilySubset = async (layerIds: string[][]) => {
            for (let i = 0; i < layerIds.length; i++) {
                await this.framework.createFontFamilySubset(this.framework.getLayers(layerIds[i]), true);
            }
        };
        createStagesFontFamilySubset(snapshot.originIdsList);
        // 设置就绪
        this.room.document.setReady();
        this.room.emitReady();
        this.room.renderUserCursor();
        this.room.renderUserPick();
    }
}
